import React, { useState, useMemo } from 'react';

import moment from 'moment';
import { makeStyles } from '@mui/styles';

import { OrdersToolbar, OrdersTable } from './components';

import CircularProgress from '@mui/material/CircularProgress';
import { Alert } from '@mui/material';

import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchTodayOrders } from 'store/actions/restaurant';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getOrders, deleteOrders as deleteOrdersAPI } from 'apis/orderAPIs';
import { transformOrderId, normalizeOrderSearchText } from 'util/orderUtils';
import { buildOrdersCsv } from 'util/orderCsv';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5)
    }
  },
  content: {
    marginTop: theme.spacing(2)
  },
  uiProgess: {
		position: 'fixed',
		zIndex: '1000',
		height: '31px',
		width: '31px',
		left: '50%',
		top: '35%'
	}
}));




const OrderList = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const history = useHistory();

  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [filterText, setFilterText] = useState(''); // Client-side filter
  
  // Local state for actual query parameters (only update when search is triggered)
  const [queryParams, setQueryParams] = useState({
    fromDate: new Date(),
    toDate: new Date()
  });

  const loadTodayOrders = (orders) => dispatch(fetchTodayOrders(orders));

  const orderType = useSelector((state) => state.restaurant.orderType);
  const fromDate = useSelector((state) => state.restaurant.orderFromDate);
  const toDate = useSelector((state) => state.restaurant.orderToDate);

  // Check if current query is for today
  const isToday = () => {
    const today = moment().format('YYYY-MM-DD');
    const queryFrom = moment(queryParams.fromDate).format('YYYY-MM-DD');
    const queryTo = moment(queryParams.toDate).format('YYYY-MM-DD');
    return queryFrom === today && queryTo === today;
  };

  // React Query hook for fetching orders by date range
  const {
    data: allOrders = [],
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['orders', orderType, queryParams.fromDate, queryParams.toDate],
    queryFn: () => getOrders(
      orderType, 
      moment(queryParams.fromDate).format('YYYY-MM-DD'), 
      moment(queryParams.toDate).format('YYYY-MM-DD'),
      null // No longer filtering by table number on server
    ),
    retry: 2,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: isToday() ? 30000 : false, // Auto-refresh every 30 seconds if viewing today's orders
    onSuccess: (data) => {
      // Update Redux store for backward compatibility
      loadTodayOrders(data);
    },
    onError: (error) => {
      console.log('Error fetching orders:', error);
      if (error.response && error.response.status === 403) {
        history.push('/sign-in');
      }
    }
  });

  // Client-side filtering by table number, customer name, or order ID
  const filteredOrders = useMemo(() => {
    const searchTerm = normalizeOrderSearchText(filterText);
    if (!searchTerm) {
      return allOrders;
    }

    // Compact form helps match pasted IDs / short codes typed with stray spaces
    const searchCompact = searchTerm.replace(/\s/g, '');

    return allOrders.filter(order => {
      const orderIdsToCheck = [
        order.id,
        order.orderNumber,
        order.reference,
        order.order?.id,
      ].filter(Boolean);

      for (const ref of orderIdsToCheck) {
        const raw = String(ref);
        const orderIdLower = raw.toLowerCase();
        const orderIdCompact = orderIdLower.replace(/[\s-]/g, '');

        if (orderIdLower.includes(searchTerm) || orderIdCompact.includes(searchCompact)) {
          return true;
        }

        const displayCode = transformOrderId(ref).toLowerCase();
        if (
          displayCode &&
          (displayCode.includes(searchTerm) ||
            displayCode.includes(searchCompact))
        ) {
          return true;
        }
      }

      // Filter by Table Number
      const tableNumber = (
        (order.restaurant && order.restaurant.tableId) || 
        order.tableNumber || 
        order.tableNo || 
        ''
      ).toString().toLowerCase();
      if (tableNumber.includes(searchTerm)) {
        return true;
      }

      // Filter by Customer Name
      const customerName = (
        (order.user && order.user.name) || 
        order.customerName || 
        ''
      ).toLowerCase();
      if (customerName.includes(searchTerm)) {
        return true;
      }

      return false;
    });
  }, [allOrders, filterText]);

  // Mutation for deleting orders
  const deleteOrdersMutation = useMutation({
    mutationFn: ({ orderIds, type, dateStr }) => deleteOrdersAPI(orderIds, type, dateStr),
    onSuccess: () => {
      // Invalidate and refetch orders after successful deletion
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setSelectedOrderIds([]);
    },
    onError: (error) => {
      console.log('Error deleting orders:', error);
      if (error.response && error.response.status === 403) {
        history.push('/sign-in');
      }
    }
  });

  const selectOrders = (ids) => {
    if (ids.length > 0) {
      setSelectedOrderIds(ids);
    }
  };

  const deleteOrders = () => {
    console.log('deleteOrders', selectedOrderIds);
    if (selectedOrderIds.length === 0) {
      return;
    }
    
    deleteOrdersMutation.mutate({
      orderIds: selectedOrderIds,
      type: orderType,
      dateStr: moment(fromDate).format('YYYY-MM-DD')
    });
  };

  const handleSearch = () => {
    // Update query parameters to trigger a new search
    setQueryParams({
      fromDate: fromDate,
      toDate: toDate
    });
  };

  const handleFilterChange = (newFilterText) => {
    setFilterText(newFilterText);
  };

  const downloadOrdersCSV = () => {
    const csvContent = buildOrdersCsv(filteredOrders);

    // Create and trigger download
    // Prepend a UTF-8 BOM so Excel decodes as UTF-8 instead of the system ANSI
    // codepage (GBK/Big5), which garbles Chinese names and addresses
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const dateStr = moment(fromDate).format('DD-MM-YY');
    link.setAttribute('download', `orders-${dateStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };




  return (
    <div className={classes.root}>
      <OrdersToolbar 
        deleteProxy={deleteOrders} 
        downloadOrders={downloadOrdersCSV} 
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
        filterText={filterText}
      />
      <div className={classes.content}>
        {isError && (
          <Alert severity="error" style={{ marginBottom: '16px' }}>
            {error?.response?.data?.message || 'Error in retrieving the data'}
          </Alert>
        )}
        {(isLoading || deleteOrdersMutation.isPending) && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
            <CircularProgress size={150} />
          </div>
        )}
        {!isLoading && !deleteOrdersMutation.isPending && (
          <OrdersTable orders={filteredOrders} selectorders={selectOrders} />
        )}
      </div>
    </div>
  );
};

export default OrderList;
